import React, { useEffect, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Collapse, Form, Modal, ProgressBar, Spinner, Table } from 'react-bootstrap';

import { GET_VINYL_STORED_ASSETS, UPLOAD_VINYL_ASSET, UPLOAD_VINYL_LEVEL_ICON, UPLOAD_VINYL_THEME } from './graphql';
import { fetchFresh, generateVinylThemes, GeneratedVinylThemeFile, looksLikeTheme } from './utils';
import AssetDisplay from '../components/AssetDisplay';

const ICON_LEVELS = [1, 2, 3];
type ThemeBuildMode = 'generate' | 'inject';
type ExistingThemeSource = { filename: string; readText: () => Promise<string> };
type StoredThemeAsset = { name: string; folder: string; url: string };

const withoutPopupIcon = (theme: Record<string, unknown>): Record<string, unknown> => {
  const clone = JSON.parse(JSON.stringify(theme)) as Record<string, unknown>;
  if (clone.screen && typeof clone.screen === 'object') {
    delete (clone.screen as Record<string, unknown>).popupIcon;
  }
  return clone;
};

const NAMESPACE = process.env.REACT_APP_NAMESPACE || 'local';
// Mirror of the backend ASSETS_BUCKET default per environment (see Sp3Be src/service/storage).
const TARGET_BUCKET = NAMESPACE === 'prod' ? 'songpop3-assets' : 'songpop3-test-assets';

// Canonical raw asset files a vinyl must ship. Step 2 blocks the upload unless the selected files
// match this list exactly — same names, same count (no missing, no extra, no duplicate names).
const EXPECTED_VINYL_ASSET_FILES = [
  'screen.Splash.character.lvl2.json',
  'screen.Splash.character.lvl3.json',
  'screen.Splash.animatedOverlay.lvl1.json',
  'screen.Splash.animatedOverlay.lvl2.json',
  'screen.Splash.animatedOverlay.lvl3.json',
  'screen.Splash.background.png',
  'screen.Splash.logo.png',
  'screen.Splash.vinylAssets.backCircle.png',
  'screen.Splash.vinylAssets.decorationIcon.png',
  'screen.Splash.vinylAssets.hand.png',
  'screen.Games.newGameButton.overlay.png',
];

type VinylAssetCheck = { missing: string[]; unexpected: string[]; duplicates: string[]; ok: boolean };

// Compares the picked files against EXPECTED_VINYL_ASSET_FILES by name: reports files that are
// required but absent (missing), picked but not on the list (unexpected), and picked more than once
// (duplicates). ok is true only when all three are empty — i.e. an exact name-and-count match.
const checkVinylAssetFiles = (files: File[]): VinylAssetCheck => {
  const expected = new Set(EXPECTED_VINYL_ASSET_FILES);
  const names = files.map((f) => f.name);
  const seen = new Set<string>();
  const duplicates: string[] = [];
  names.forEach((name) => {
    if (seen.has(name)) duplicates.push(name);
    seen.add(name);
  });
  const missing = EXPECTED_VINYL_ASSET_FILES.filter((name) => !seen.has(name));
  const unexpected = names.filter((name) => !expected.has(name));
  return { missing, unexpected, duplicates, ok: !missing.length && !unexpected.length && !duplicates.length };
};

// Vinyl folder convention: themeName + _MM_YYYY (e.g. anniversary2026_07_2026), based on the current date.
const deriveVinylFolder = (theme: string): string => {
  if (!theme) return '';
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  return `${theme}_${mm}_${yyyy}`;
};

const downloadFile = (file: GeneratedVinylThemeFile) => {
  const blob = new Blob([file.content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const StepHeading: React.FC<{ n: number; title: string; children?: React.ReactNode }> = ({ n, title, children }) => (
  <div className="d-flex justify-content-between align-items-center mb-2">
    <h6 className="mb-0 text-uppercase text-muted">
      <Badge variant="secondary" className="mr-2">
        {n}
      </Badge>
      {title}
    </h6>
    {children}
  </div>
);

type VinylThemeGeneratorProps = {
  /** Vinyl item id used only for base theme uploads; level icons follow the editable vinylFolder. */
  itemId?: string;
  /** Called after theme JSON files are uploaded, with each generated file and its resulting URL. */
  onThemeUploaded?: (files: { filename: string; url: string }[]) => void;
};

const VinylThemeGenerator: React.FC<VinylThemeGeneratorProps> = ({ itemId, onThemeUploaded }) => {
  const apolloClient = useApolloClient();
  const [open, setOpen] = useState<boolean>(true);

  const [figmaToken, setFigmaToken] = useState<Record<string, unknown> | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [themeOptions, setThemeOptions] = useState<string[]>([]);
  const [themeName, setThemeName] = useState<string>('');
  const [vinylFolder, setVinylFolder] = useState<string>('');
  const [generated, setGenerated] = useState<GeneratedVinylThemeFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadedUrls, setUploadedUrls] = useState<string[] | null>(null);
  const [themeBuildMode, setThemeBuildMode] = useState<ThemeBuildMode>('generate');
  const [loadingUploadedThemes, setLoadingUploadedThemes] = useState<boolean>(false);
  const [uploadedThemeLoadMessage, setUploadedThemeLoadMessage] = useState<string | null>(null);

  const [assetFiles, setAssetFiles] = useState<File[]>([]);
  const [assetUrls, setAssetUrls] = useState<string[] | null>(null);
  const [assetProgress, setAssetProgress] = useState<{ done: number; total: number } | null>(null);
  const [previewFile, setPreviewFile] = useState<GeneratedVinylThemeFile | null>(null);

  const [uploadVinylTheme, { loading: uploading }] = useMutation(UPLOAD_VINYL_THEME);
  const [uploadVinylAsset] = useMutation(UPLOAD_VINYL_ASSET);
  const [assetUploading, setAssetUploading] = useState<boolean>(false);

  const [uploadVinylLevelIcon] = useMutation(UPLOAD_VINYL_LEVEL_ICON);
  const [iconFiles, setIconFiles] = useState<Record<number, File | null>>({ 1: null, 2: null, 3: null });
  const [iconUrls, setIconUrls] = useState<Record<number, string | null>>({ 1: null, 2: null, 3: null });
  const [iconUploadingLevel, setIconUploadingLevel] = useState<number | null>(null);
  const [loadingIconPreviews, setLoadingIconPreviews] = useState<boolean>(false);

  const resetFileState = () => {
    setFigmaToken(null);
    setFileName('');
    setThemeOptions([]);
    setThemeName('');
  };

  const selectTheme = (theme: string) => {
    setThemeName(theme);
    // Keep the vinyl folder in sync with the selected theme (still editable afterwards).
    setVinylFolder(deriveVinylFolder(theme));
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setGenerated([]);
    setUploadedUrls(null);
    const file = (event.target.files ?? [])[0];
    if (!file) {
      resetFileState();
      return;
    }
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as Record<string, unknown>;
      // Theme names are the top-level keys of the figmaToken file (matches the CLI's figmaToken[themeName]),
      // but only the ones whose value actually looks like a theme (skips Tokens Studio $themes/$metadata etc.).
      const options = Object.keys(parsed).filter((key) => looksLikeTheme(parsed[key]));
      if (options.length === 0) {
        resetFileState();
        setError(
          `No themes found in "${file.name}". Expected top-level keys whose value contains "primary100" and "gradients".`
        );
        return;
      }
      setFigmaToken(parsed);
      setFileName(file.name);
      setThemeOptions(options);
      // Default to the bottom-most theme (last in the file — usually the most recently added).
      selectTheme(options[options.length - 1]);
    } catch (e) {
      resetFileState();
      setError(`Unable to read/parse "${file.name}" as JSON.`);
    }
  };

  const findVinylAssetBaseUrl = (node: unknown): string | null => {
    if (typeof node === 'string') {
      const match = node.match(/https:\/\/storage\.googleapis\.com\/[^/]+\/vinyls\/[^/]+/);
      return match ? match[0] : null;
    }
    if (Array.isArray(node)) {
      for (const child of node) {
        const found = findVinylAssetBaseUrl(child);
        if (found) return found;
      }
      return null;
    }
    if (node && typeof node === 'object') {
      for (const child of Object.values(node)) {
        const found = findVinylAssetBaseUrl(child);
        if (found) return found;
      }
    }
    return null;
  };

  const getPopupIconUrl = (level: number, existingTheme?: Record<string, unknown>) => {
    if (iconUrls[level]) return iconUrls[level];
    // No new icon uploaded: keep the theme's current popupIcon if it has one, otherwise leave it empty ("").
    // We do NOT fall back to char{level}.png — that file usually doesn't exist and would be a broken (404) URL.
    const existing = existingTheme ? getPopupIconFromTheme(existingTheme) : null;
    return existing ?? '';
  };

  const getVinylFolderFromBaseUrl = (baseUrl: string): string | null => {
    const match = baseUrl.match(/\/vinyls\/([^/]+)$/);
    return match ? match[1] : null;
  };

  const getPopupIconFromTheme = (theme: Record<string, unknown>): string | null => {
    const screen = theme.screen;
    if (!screen || typeof screen !== 'object') return null;
    const popupIcon = (screen as Record<string, unknown>).popupIcon;
    return typeof popupIcon === 'string' ? popupIcon : null;
  };

  const handleGenerate = () => {
    setError(null);
    setUploadedUrls(null);
    setUploadedThemeLoadMessage(null);
    if (!figmaToken) {
      setError('Please choose a figmaToken.json file first.');
      return;
    }
    if (!themeName.trim() || !vinylFolder.trim()) {
      setError('Theme name and Vinyl folder are both required.');
      return;
    }
    try {
      setGenerated(generateVinylThemes(figmaToken, themeName.trim(), vinylFolder.trim(), TARGET_BUCKET, iconUrls));
    } catch (e) {
      setGenerated([]);
      setError(e instanceof Error ? e.message : 'Failed to generate theme files.');
    }
  };

  const getLevelFromFilename = (filename: string): number | null => {
    const match = filename.match(/(?:lvl|level)\s*([1-3])/i);
    if (!match) return null;
    return Number(match[1]);
  };

  const injectExistingThemeSources = async (sources: ExistingThemeSource[]) => {
    const injected = await Promise.all(
      sources.map(async (source): Promise<GeneratedVinylThemeFile> => {
        const level = getLevelFromFilename(source.filename);
        if (!level) {
          throw new Error(`Cannot detect level from "${source.filename}". Expected Lvl1, Lvl2, or Lvl3 in the filename.`);
        }
        const parsed = JSON.parse(await source.readText()) as Record<string, unknown>;
        if (!parsed || typeof parsed !== 'object') {
          throw new Error(`"${source.filename}" is not a JSON object.`);
        }
        const originalWithoutPopupIcon = withoutPopupIcon(parsed);
        const screen = parsed.screen && typeof parsed.screen === 'object' ? parsed.screen : {};
        parsed.screen = {
          ...screen,
          popupIcon: getPopupIconUrl(level, parsed),
        };
        if (JSON.stringify(withoutPopupIcon(parsed)) !== JSON.stringify(originalWithoutPopupIcon)) {
          throw new Error(`Inject would change more than screen.popupIcon in "${source.filename}".`);
        }
        return {
          filename: source.filename,
          content: JSON.stringify(parsed, null, 2),
          data: parsed,
        };
      })
    );
    setGenerated(
      injected.sort((a, b) => (getLevelFromFilename(a.filename) ?? 0) - (getLevelFromFilename(b.filename) ?? 0))
    );
  };

  const handleLoadUploadedThemes = async () => {
    setError(null);
    setUploadedUrls(null);
    setUploadedThemeLoadMessage(null);
    const folder = (itemId ?? '').trim();
    if (!folder) {
      setError('Missing vinyl item id for loading uploaded theme JSON files.');
      return;
    }
    setLoadingUploadedThemes(true);
    try {
      const { data } = await apolloClient.query({
        query: GET_VINYL_STORED_ASSETS,
        variables: { itemId: folder },
        fetchPolicy: 'no-cache',
      });
      const files: StoredThemeAsset[] = data?.vinylStoredAssets?.files ?? [];
      const themeFiles = files
        .filter((file) => file.name.toLowerCase().endsWith('.json'))
        .filter((file) => getLevelFromFilename(file.name) !== null);
      if (themeFiles.length === 0) {
        const jsonFiles = files
          .filter((file) => file.name.toLowerCase().endsWith('.json'))
          .map((file) => file.name);
        throw new Error(
          jsonFiles.length > 0
            ? `Found JSON file(s), but none had Lvl1, Lvl2, or Lvl3 in the filename: ${jsonFiles.join(', ')}.`
            : `No uploaded theme JSON files found under sp3/vinyls/${folder}/.`
        );
      }
      await injectExistingThemeSources(
        themeFiles.map((file) => ({
          filename: file.name,
          readText: async () => {
            const response = await fetchFresh(file.url);
            if (!response.ok) throw new Error(`Could not load ${file.name} (${response.status}).`);
            return response.text();
          },
        }))
      );
      setUploadedThemeLoadMessage(
        `Loaded ${themeFiles.length} theme file(s): ${themeFiles.map((f) => f.name).join(', ')}`
      );
    } catch (e) {
      setGenerated([]);
      setError(e instanceof Error ? e.message : 'Failed to load uploaded theme files.');
    } finally {
      setLoadingUploadedThemes(false);
    }
  };

  const inferVinylFolderFromUploadedThemes = async (): Promise<string | null> => {
    const folder = (itemId ?? '').trim();
    if (!folder) return null;

    const { data } = await apolloClient.query({
      query: GET_VINYL_STORED_ASSETS,
      variables: { itemId: folder },
      fetchPolicy: 'no-cache',
    });
    const files: StoredThemeAsset[] = data?.vinylStoredAssets?.files ?? [];
    const themeFile = files
      .filter((file) => file.name.toLowerCase().endsWith('.json'))
      .find((file) => getLevelFromFilename(file.name) !== null);
    if (!themeFile) return null;

    const response = await fetchFresh(themeFile.url);
    if (!response.ok) throw new Error(`Could not load ${themeFile.name} (${response.status}).`);
    const parsed = JSON.parse(await response.text()) as Record<string, unknown>;
    const baseUrl = findVinylAssetBaseUrl(parsed);
    return baseUrl ? getVinylFolderFromBaseUrl(baseUrl) : null;
  };

  // silent = auto-load on mount: don't surface "missing id"/"not found"/network errors as an alert.
  const loadIconPreviews = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setError(null);
    const folder = (itemId ?? '').trim();
    if (!folder) {
      if (!silent) setError('Missing vinyl item id for loading uploaded level icons.');
      return;
    }
    setLoadingIconPreviews(true);
    try {
      const { data } = await apolloClient.query({
        query: GET_VINYL_STORED_ASSETS,
        variables: { itemId: folder },
        fetchPolicy: 'no-cache',
      });
      const files: StoredThemeAsset[] = data?.vinylStoredAssets?.files ?? [];
      const nextIconUrls: Record<number, string | null> = { 1: null, 2: null, 3: null };
      let inferredAssetBase: string | null = null;

      const themeFiles = files
        .filter((file) => file.name.toLowerCase().endsWith('.json'))
        .filter((file) => getLevelFromFilename(file.name) !== null);
      await Promise.all(
        themeFiles.map(async (file) => {
          const level = getLevelFromFilename(file.name);
          if (!level) return;
          const response = await fetchFresh(file.url);
          if (!response.ok) throw new Error(`Could not load ${file.name} (${response.status}).`);
          const parsed = JSON.parse(await response.text()) as Record<string, unknown>;
          nextIconUrls[level] = getPopupIconFromTheme(parsed);
          inferredAssetBase = inferredAssetBase || findVinylAssetBaseUrl(parsed);
        })
      );

      // Old vinyls open with no theme selected, so the Vinyl folder field starts empty. Infer it from
      // where the assets are actually stored (the base asset URL embedded in the uploaded theme JSON)
      // and prefill the field — only while still empty, so a later theme selection or manual edit wins.
      if (inferredAssetBase) {
        const inferredFolder = getVinylFolderFromBaseUrl(inferredAssetBase);
        if (inferredFolder) setVinylFolder((prev) => prev || inferredFolder);
      }

      if (!Object.values(nextIconUrls).some(Boolean) && inferredAssetBase) {
        const assetFolder = getVinylFolderFromBaseUrl(inferredAssetBase);
        const storedIconFiles = assetFolder
          ? files.filter(
              (file) =>
                file.folder === `vinyls/${assetFolder}/` &&
                /^vinylPreviewIcon_[1-3]\.(png|json)$/i.test(file.name)
            )
          : [];
        storedIconFiles.forEach((file) => {
          const level = getLevelFromFilename(file.name.replace('vinylPreviewIcon_', 'Lvl'));
          if (level) nextIconUrls[level] = file.url;
        });
      }

      if (!Object.values(nextIconUrls).some(Boolean)) {
        if (silent) return;
        throw new Error('No uploaded level icon previews found.');
      }
      setIconUrls(nextIconUrls);
    } catch (e) {
      if (!silent) setError(e instanceof Error ? e.message : 'Failed to load uploaded level icon previews.');
    } finally {
      setLoadingIconPreviews(false);
    }
  };

  // Auto-load previously uploaded level-icon previews when the editor opens (the component is keyed
  // by item id, so this runs once per vinyl). Silent: no alert if the vinyl has no icons yet.
  useEffect(() => {
    if (itemId) loadIconPreviews({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const handlePrepareThemeFiles = () => {
    if (themeBuildMode === 'generate') {
      handleGenerate();
      return;
    }
    handleLoadUploadedThemes();
  };

  const handleUpload = async () => {
    setError(null);
    setUploadedUrls(null);
    try {
      // Theme JSON (base item) goes to the item's own folder (sp3/vinyls/{item.id}/), matching the old
      // base-item asset path. The embedded asset URLs and the raw assets keep using vinylFolder.
      const themeFolder = (itemId ?? '').trim() || vinylFolder.trim();
      const { data } = await uploadVinylTheme({
        variables: {
          input: {
            vinylFolder: themeFolder,
            files: generated.map(({ filename, content }) => ({ filename, content })),
          },
        },
      });
      const urls: string[] = data?.uploadVinylTheme?.urls ?? [];
      setUploadedUrls(urls);
      // urls line up with the generated files (level 1, 2, 3). Report them so the parent can, e.g.,
      // fill the base-item Asset field with the level-1 URL.
      if (onThemeUploaded) {
        onThemeUploaded(
          generated.map((file, index) => ({ filename: file.filename, url: urls[index] })).filter((f) => !!f.url)
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    }
  };

  const handleAssetFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAssetUrls(null);
    setAssetFiles(Array.from(event.target.files ?? []));
  };

  const handleAssetUpload = async () => {
    setError(null);
    setAssetUrls(null);
    if (!vinylFolder.trim()) {
      setError('Set the Vinyl folder before uploading assets.');
      return;
    }
    if (assetFiles.length === 0) {
      setError('Choose at least one asset file to upload.');
      return;
    }
    const check = checkVinylAssetFiles(assetFiles);
    if (!check.ok) {
      const parts = [
        check.missing.length ? `Missing: ${check.missing.join(', ')}.` : '',
        check.unexpected.length ? `Unexpected: ${check.unexpected.join(', ')}.` : '',
        check.duplicates.length ? `Duplicated: ${check.duplicates.join(', ')}.` : '',
      ].filter(Boolean);
      setError(
        `Selected files must match the required ${EXPECTED_VINYL_ASSET_FILES.length}-file list exactly. ${parts.join(' ')}`.trim()
      );
      return;
    }
    // The server's graphql-upload middleware is capped at maxFiles: 1, so upload one file per request.
    setAssetUploading(true);
    setAssetProgress({ done: 0, total: assetFiles.length });
    const urls: string[] = [];
    try {
      for (let i = 0; i < assetFiles.length; i++) {
        const { data } = await uploadVinylAsset({
          variables: { input: { vinylFolder: vinylFolder.trim(), file: assetFiles[i] } },
        });
        if (data?.uploadVinylAsset?.url) urls.push(data.uploadVinylAsset.url);
        setAssetProgress({ done: i + 1, total: assetFiles.length });
      }
      setAssetUrls(urls);
    } catch (e) {
      setAssetUrls(urls);
      setError(
        `Asset upload failed after ${urls.length}/${assetFiles.length} file(s): ${
          e instanceof Error ? e.message : 'unknown error'
        }`
      );
    } finally {
      setAssetUploading(false);
    }
  };

  const handleIconFileChange = (level: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = (event.target.files ?? [])[0] ?? null;
    setIconFiles((prev) => ({ ...prev, [level]: file }));
  };

  const handleIconUpload = async (level: number) => {
    const file = iconFiles[level];
    if (!file) {
      setError(`Choose a file for level ${level}.`);
      return;
    }
    setError(null);
    setIconUploadingLevel(level);
    try {
      const folder = vinylFolder.trim() || (await inferVinylFolderFromUploadedThemes());
      if (!folder) {
        throw new Error('Could not infer Vinyl folder from uploaded theme JSON.');
      }
      if (!vinylFolder.trim()) setVinylFolder(folder);
      const { data } = await uploadVinylLevelIcon({
        variables: { input: { folder, level, file } },
      });
      setIconUrls((prev) => ({ ...prev, [level]: data?.uploadVinylLevelIcon?.url ?? null }));
    } catch (e) {
      setError(e instanceof Error ? e.message : `Level ${level} icon upload failed.`);
    } finally {
      setIconUploadingLevel(null);
    }
  };

  const folderLabel = vinylFolder || '{vinylFolder}';

  const uploadedUrlList = (urls: string[]) => (
    <ul className="mb-0 mt-2 small">
      {urls.map((url) => (
        <li key={url}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </li>
      ))}
    </ul>
  );

  const assetCheck = checkVinylAssetFiles(assetFiles);

  return (
    <Card className="mb-4">
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        <h5 className="mb-0">
          <i className={`fas fa-chevron-${open ? 'down' : 'right'} mr-2`} />
          Vinyl Theme
        </h5>
        <span className="text-muted small">
          {NAMESPACE} → <code>{TARGET_BUCKET}</code>
        </span>
      </Card.Header>

      <Collapse in={open}>
        <div>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Step 1 — Source */}
            <StepHeading n={1} title="Source" />
            <Form.Group controlId="figma-token-file">
              <Form.Label>figmaToken.json</Form.Label>
              <Form.Control type="file" accept="application/json,.json" onChange={handleFileChange} />
              {fileName && <Form.Text className="text-success">Loaded: {fileName}</Form.Text>}
            </Form.Group>
            <Form.Row>
              <Form.Group as={Col} md={6} controlId="theme-name">
                <Form.Label>Theme</Form.Label>
                <Form.Control
                  as="select"
                  value={themeName}
                  disabled={themeOptions.length === 0}
                  onChange={(e) => selectTheme(e.target.value)}
                >
                  <option value="">{themeOptions.length === 0 ? 'Upload a file first' : 'Select a theme…'}</option>
                  {themeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted">Filename prefix.</Form.Text>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="vinyl-folder">
                <Form.Label>Vinyl folder</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. anniversary2026_07_2026"
                  value={vinylFolder}
                  onChange={(e) => setVinylFolder(e.target.value)}
                />
                <Form.Text className="text-muted">Auto: theme_MM_YYYY — editable.</Form.Text>
              </Form.Group>
            </Form.Row>

            <hr />

            {/* Step 2 — Vinyl assets */}
            <StepHeading n={2} title="Vinyl assets (PNG / JSON)">
              <Button
                type="button"
                variant="success"
                size="sm"
                onClick={handleAssetUpload}
                disabled={assetUploading || assetFiles.length === 0 || !vinylFolder.trim() || !assetCheck.ok}
              >
                {assetUploading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="mr-2" /> Uploading…
                  </>
                ) : (
                  <>Upload {assetFiles.length || ''} asset(s)</>
                )}
              </Button>
            </StepHeading>
            <Form.Group controlId="vinyl-assets-file">
              <Form.Control
                type="file"
                multiple
                accept=".png,.json,image/png,application/json"
                onChange={handleAssetFilesChange}
              />
              <Form.Text className="text-muted">
                Select exactly the {EXPECTED_VINYL_ASSET_FILES.length} required files — names and count must match.
              </Form.Text>
            </Form.Group>
            {assetFiles.length > 0 && (
              <ul className="text-muted small">
                {assetFiles.map((file) => {
                  const unexpected = !EXPECTED_VINYL_ASSET_FILES.includes(file.name);
                  return (
                    <li key={file.name}>
                      <code>{file.name}</code> ({Math.ceil(file.size / 1024)} KB){' '}
                      {unexpected ? (
                        <Badge variant="danger">not in list</Badge>
                      ) : (
                        <Badge variant="success">ok</Badge>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {assetFiles.length > 0 && !assetCheck.ok && (
              <Alert variant="danger" className="py-2 small">
                Selected files must match the required {EXPECTED_VINYL_ASSET_FILES.length}-file list exactly.
                {assetCheck.missing.length > 0 && (
                  <div className="mt-1">
                    <strong>Missing ({assetCheck.missing.length}):</strong> {assetCheck.missing.join(', ')}
                  </div>
                )}
                {assetCheck.unexpected.length > 0 && (
                  <div className="mt-1">
                    <strong>Unexpected ({assetCheck.unexpected.length}):</strong> {assetCheck.unexpected.join(', ')}
                  </div>
                )}
                {assetCheck.duplicates.length > 0 && (
                  <div className="mt-1">
                    <strong>Duplicated ({assetCheck.duplicates.length}):</strong> {assetCheck.duplicates.join(', ')}
                  </div>
                )}
              </Alert>
            )}
            {assetFiles.length > 0 && assetCheck.ok && (
              <Alert variant="success" className="py-2 small">
                All {EXPECTED_VINYL_ASSET_FILES.length} required asset files present.
              </Alert>
            )}
            {assetUploading && assetProgress && (
              <ProgressBar
                animated
                now={(assetProgress.done / assetProgress.total) * 100}
                label={`${assetProgress.done}/${assetProgress.total}`}
                className="mb-2"
              />
            )}
            {assetUrls && (
              <Alert variant="success" className="mb-0">
                Uploaded {assetUrls.length} asset(s):
                {uploadedUrlList(assetUrls)}
              </Alert>
            )}

            <hr />

            {/* Step 3 — Popup icons */}
            <StepHeading n={3} title="Popup icons (per level)">
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={() => loadIconPreviews()}
                disabled={loadingIconPreviews || !itemId}
              >
                {loadingIconPreviews ? (
                  <Spinner as="span" animation="border" size="sm" className="mr-2" />
                ) : (
                  <i className="fas fa-sync-alt mr-2" />
                )}
                Load previews
              </Button>
            </StepHeading>
            <p className="text-muted small">
              Uploads to <code>vinyls/{folderLabel}/</code> using the original filename.
            </p>
            {ICON_LEVELS.map((level) => (
              <Form.Row key={level} className="align-items-center mb-3">
                <Col xs="auto">
                  <strong>Lvl {level}</strong>
                </Col>
                <Col md={5}>
                  <Form.Control
                    type="file"
                    accept=".png,.json,image/png,application/json"
                    onChange={handleIconFileChange(level)}
                  />
                </Col>
                <Col xs="auto">
                  <Button
                    type="button"
                    variant="success"
                    size="sm"
                    onClick={() => handleIconUpload(level)}
                    disabled={iconUploadingLevel === level || !iconFiles[level]}
                  >
                    {iconUploadingLevel === level ? <Spinner as="span" animation="border" size="sm" /> : 'Upload'}
                  </Button>
                </Col>
                <Col md={4}>{iconUrls[level] && <AssetDisplay assetUrl={iconUrls[level] as string} />}</Col>
              </Form.Row>
            ))}

            <hr />

            {/* Step 4 — Theme level files */}
            <StepHeading n={4} title="Theme level files">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handlePrepareThemeFiles}
                disabled={uploading || assetUploading}
              >
                {themeBuildMode === 'generate' ? 'Create theme level file' : 'Inject popupIcon'}
              </Button>
            </StepHeading>
            <Form.Group controlId="theme-build-mode">
              <Form.Label>Mode</Form.Label>
              <Form.Control
                as="select"
                value={themeBuildMode}
                onChange={(e) => {
                  setThemeBuildMode(e.target.value as ThemeBuildMode);
                  setGenerated([]);
                  setUploadedUrls(null);
                  setUploadedThemeLoadMessage(null);
                }}
              >
                <option value="generate">Generate new from figmaToken</option>
                <option value="inject">Inject popupIcon into existing theme JSON</option>
              </Form.Control>
            </Form.Group>
            {themeBuildMode === 'inject' && (
              <div className="d-flex align-items-center mb-3">
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={handleLoadUploadedThemes}
                  disabled={loadingUploadedThemes || !itemId}
                >
                  {loadingUploadedThemes ? (
                    <Spinner as="span" animation="border" size="sm" className="mr-2" />
                  ) : (
                    <i className="fas fa-cloud-download-alt mr-2" />
                  )}
                  Load uploaded theme JSON
                </Button>
                {itemId && (
                  <span className="text-muted small ml-2">
                    from <code>sp3/vinyls/{itemId}/</code>
                  </span>
                )}
              </div>
            )}
            {themeBuildMode === 'inject' && uploadedThemeLoadMessage && (
              <Alert variant="success" className="py-2">
                {uploadedThemeLoadMessage}
              </Alert>
            )}
            {generated.length === 0 ? (
              <p className="text-muted small mb-0">
                {themeBuildMode === 'generate' ? (
                  <>
                    Creates the 3 level JSON files, then upload them to{' '}
                    <code>sp3/vinyls/{itemId || folderLabel}/</code> (base-item level asset).
                  </>
                ) : (
                  <>
                    Injects <code>screen.popupIcon</code> into existing theme JSON files, then uploads them to{' '}
                    <code>sp3/vinyls/{itemId || folderLabel}/</code>.
                  </>
                )}
              </p>
            ) : (
              <>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th style={{ width: 200 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generated.map((file) => (
                      <tr key={file.filename}>
                        <td>
                          <code>{file.filename}</code>
                        </td>
                        <td>
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            className="mr-2"
                            onClick={() => setPreviewFile(file)}
                          >
                            Preview
                          </Button>
                          <Button type="button" variant="outline-secondary" size="sm" onClick={() => downloadFile(file)}>
                            Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-end">
                  <Button type="button" variant="success" onClick={handleUpload} disabled={uploading}>
                    {uploading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="mr-2" /> Uploading…
                      </>
                    ) : (
                      <>Upload theme to {TARGET_BUCKET}</>
                    )}
                  </Button>
                </div>
                {uploadedUrls && (
                  <Alert variant="success" className="mt-2 mb-0">
                    Uploaded {uploadedUrls.length} theme file(s):
                    {uploadedUrlList(uploadedUrls)}
                  </Alert>
                )}
              </>
            )}
          </Card.Body>
        </div>
      </Collapse>

      <Modal show={previewFile !== null} onHide={() => setPreviewFile(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            <code>{previewFile?.filename}</code>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre style={{ maxHeight: '65vh', overflow: 'auto', marginBottom: 0 }}>{previewFile?.content}</pre>
        </Modal.Body>
        <Modal.Footer>
          {previewFile && (
            <Button type="button" variant="outline-secondary" onClick={() => downloadFile(previewFile)}>
              Download
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => setPreviewFile(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default VinylThemeGenerator;
